import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';
import type { CSSProperties, ReactNode } from 'react';
import { useLayoutEffect, useState } from 'react';

import type { CommandPaletteOptionMeta } from './command-palette.models';
import { getOptionId } from './command-palette.utils';
import s from './command-palette-listbox.scss';

export interface CommandPaletteListboxProps<T> {
  activeIndex: number;
  ariaLabel: string;
  className?: string;
  items: T[];
  listboxId: string;
  renderItem: (item: T, meta: CommandPaletteOptionMeta) => ReactNode;
}

/**
 * A keyboard-navigable listbox with a single highlight that slides to the
 * active option. Rows can vary in height, so it measures the active option
 * rather than assume a uniform size. Scope-agnostic — the caller renders each
 * option via `renderItem`.
 */
export const CommandPaletteListbox = <T,>({
  activeIndex,
  ariaLabel,
  className,
  items,
  listboxId,
  renderItem,
}: CommandPaletteListboxProps<T>) => {
  useStyles(s);

  const [highlight, setHighlight] = useState<{
    height: number;
    y: number;
  } | null>(null);

  useLayoutEffect(() => {
    const active = document.getElementById(getOptionId(listboxId, activeIndex));

    if (active) {
      setHighlight({ height: active.offsetHeight, y: active.offsetTop });
    }
  }, [activeIndex, items, listboxId]);

  return (
    <div
      aria-label={ariaLabel}
      className={classNames(s.listbox, className)}
      id={listboxId}
      role="listbox"
    >
      {highlight && (
        <div
          className={s.highlight}
          style={
            {
              '--command-palette-highlight-height': `${highlight.height}px`,
              '--command-palette-highlight-y': `${highlight.y}px`,
            } as CSSProperties
          }
          aria-hidden
        />
      )}

      {items.map((item, index) =>
        renderItem(item, {
          active: index === activeIndex,
          id: getOptionId(listboxId, index),
          index,
        }),
      )}
    </div>
  );
};

export default CommandPaletteListbox;
